import { FetchError } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { fetchQuery, sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { customerGroupsQueryKeys } from "./customer-groups"
import { productsQueryKeys } from "./products"
import {
  processItems,
  createFieldExtractors,
  BaseFilterConfig,
} from "../../utils/client-side-filtering"

const PRICE_LISTS_QUERY_KEY = "price-lists" as const
export const priceListsQueryKeys = queryKeysFactory(PRICE_LISTS_QUERY_KEY)

export const usePriceList = (
  id: string,
  query?: Record<string, string | number>,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminPriceListResponse,
      FetchError,
      any,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery(`/vendor/price-lists/${id}`, {
        method: "GET",
        query,
      }),
    queryKey: priceListsQueryKeys.detail(id, query),
    ...options,
  })

  return { ...data, ...rest }
}

export const usePriceListProducts = (
  id: string,
  query?: Record<string, string | number>,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminPriceListResponse,
      FetchError,
      any,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery(`/vendor/price-lists/${id}/products`, {
        method: "GET",
        query,
      }),
    queryKey: [PRICE_LISTS_QUERY_KEY, id, "products"],
    ...options,
  })

  return { ...data, ...rest }
}

export const usePriceLists = (
  query?: HttpTypes.AdminPriceListListParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminPriceListListResponse,
      FetchError,
      HttpTypes.AdminPriceListListResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >,
  filters?: BaseFilterConfig
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery("/vendor/price-lists", {
        method: "GET",
        query: { limit: 1000 },
      }),
    queryKey: priceListsQueryKeys.list(query),
    ...options,
  })

  const searchableFields = {
    title: createFieldExtractors.string("title"),
    description: createFieldExtractors.string("description"),
  }

  const sortableFields = {
    title: (item: any) => item?.title,
    status: (item: any) => item?.status,
    type: (item: any) => item?.type,
    created_at: (item: any) => item?.created_at,
    updated_at: (item: any) => item?.updated_at,
  }

  let price_lists = data?.price_lists || []

  if (filters && (filters.q || filters.order)) {
    price_lists = processItems(
      price_lists,
      filters,
      searchableFields,
      sortableFields
    )
  }

  return {
    ...data,
    price_lists,
    count: price_lists.length,
    ...rest,
  }
}

export const useCreatePriceList = (
  options?: UseMutationOptions<
    HttpTypes.AdminPriceListResponse,
    FetchError,
    HttpTypes.AdminCreatePriceList
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery("/vendor/price-lists", {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.lists(),
      })

      queryClient.invalidateQueries({
        queryKey: customerGroupsQueryKeys.all,
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdatePriceList = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminPriceListResponse,
    FetchError,
    HttpTypes.AdminUpdatePriceList
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/price-lists/${id}`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.details(),
      })

      queryClient.invalidateQueries({
        queryKey: customerGroupsQueryKeys.all,
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeletePriceList = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminPriceListDeleteResponse,
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () =>
      fetchQuery(`/vendor/price-lists/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const usePriceListLinkProducts = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminPriceListResponse,
    FetchError,
    HttpTypes.AdminLinkPriceListProducts & { create?: any[]; update?: any[] }
  >
) => {
  return useMutation({
    mutationFn: (payload) => {
      if (payload.create) {
        return fetchQuery(`/vendor/price-lists/${id}/prices/batch`, {
          method: "POST",
          body: payload,
        })
      }
      return fetchQuery(`/vendor/price-lists/${id}/products`, {
        method: "POST",
        body: payload,
      })
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.detail(id),
      })
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useBatchPriceListPrices = (
  id: string,
  query?: Record<string, string | number>,
  options?: UseMutationOptions<
    HttpTypes.AdminPriceListBatchResponse,
    FetchError,
    {
      create?: HttpTypes.AdminCreatePriceListPrice[]
      update?: HttpTypes.AdminUpdatePriceListPrice[]
      delete?: string[]
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/price-lists/${id}/prices/batch`, {
        method: "POST",
        body: payload,
        query,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.detail(id),
      })
      queryClient.invalidateQueries({
        queryKey: priceListsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
